import { useState } from 'react';
import type { EquipmentInput, Resource } from '../lib/types';

/**
 * Add or edit a catalogue entry. Admin-only — the caller decides
 * whether to render it.
 *
 * `status` is a free-text field backed by a datalist of values already
 * in use. The vocabulary came from a SharePoint choice column, so we
 * don't get to invent entries for it — but a plain dropdown of
 * observed values would make the first "Out of Service" row
 * impossible to create.
 */
export function EquipmentModal({
  resource,
  statusOptions,
  bookingCount,
  saving,
  onSave,
  onClose,
}: {
  /** null = create. */
  resource: Resource | null;
  statusOptions: string[];
  /** Bookings currently pointing at this resource, for the rename note. */
  bookingCount: number;
  saving: boolean;
  onSave: (input: EquipmentInput) => void;
  onClose: () => void;
}) {
  const isEdit = resource !== null;

  const [form, setForm] = useState<EquipmentInput>({
    title: resource?.title ?? '',
    name: resource?.name ?? '',
    type: resource?.type ?? 'Equipment',
    category: resource?.category ?? '',
    office: resource?.office ?? '',
    status: resource?.status ?? 'Available',
    nextDue: resource?.nextDue ?? '',
    calStatus: resource?.calStatus ?? '',
    notes: resource?.notes ?? '',
    isActive: resource?.isActive ?? true,
  });
  const update = <K extends keyof EquipmentInput>(
    key: K,
    value: EquipmentInput[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const isEquipment = form.type === 'Equipment';
  const renaming = isEdit && form.title.trim() !== resource.title;
  const ready =
    form.title.trim() !== '' &&
    form.name.trim() !== '' &&
    form.status.trim() !== '';

  const statuses = [...new Set([...statusOptions, 'Available', form.status])]
    .filter(Boolean)
    .sort();

  return (
    <div className="modal-overlay is-nested" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span
            style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}
          >
            {isEdit ? 'Edit resource' : 'Add resource'}
          </span>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="form-grid">
          <div>
            <label className="form-label">Type</label>
            <select
              className="form-input"
              value={form.type}
              onChange={(e) =>
                update('type', e.target.value as EquipmentInput['type'])
              }
            >
              <option>Equipment</option>
              <option>Personnel</option>
            </select>
          </div>
          <div>
            <label className="form-label">Status</label>
            <input
              className="form-input"
              list="equipment-status-options"
              value={form.status}
              onChange={(e) => update('status', e.target.value)}
            />
            <datalist id="equipment-status-options">
              {statuses.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <div
              style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}
            >
              Only “Available” is bookable.
            </div>
          </div>

          <div>
            <label className="form-label">
              {isEquipment ? 'Asset tag' : 'Identifier'}
            </label>
            <input
              className="form-input"
              placeholder={isEquipment ? 'e.g. EM-0142' : 'e.g. jsmith'}
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">Name</label>
            <input
              className="form-input"
              placeholder={isEquipment ? 'Romer Absolute Arm' : 'Jane Smith'}
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
            />
          </div>

          <div>
            <label className="form-label">
              {isEquipment ? 'Category' : 'Role'}
            </label>
            <input
              className="form-input"
              placeholder="Optional"
              value={form.category}
              onChange={(e) => update('category', e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">Office</label>
            <input
              className="form-input"
              placeholder="Optional"
              value={form.office}
              onChange={(e) => update('office', e.target.value)}
            />
          </div>

          {isEquipment && (
            <>
              <div>
                <label className="form-label">Next calibration due</label>
                <input
                  type="date"
                  className="form-input"
                  value={form.nextDue}
                  onChange={(e) => update('nextDue', e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">Calibration status</label>
                <select
                  className="form-input"
                  value={form.calStatus}
                  onChange={(e) => update('calStatus', e.target.value)}
                >
                  <option value="">Derive from due date</option>
                  <option>Current</option>
                  <option>Due Soon</option>
                  <option>Overdue</option>
                </select>
              </div>
            </>
          )}

          <div className="form-full">
            <label className="form-label">Notes</label>
            <input
              className="form-input"
              placeholder="Optional"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
            />
          </div>
        </div>

        {isEdit && (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: 'var(--text-secondary)',
              marginBottom: 10,
            }}
          >
            <input
              type="checkbox"
              style={{ width: 'auto', padding: 0 }}
              checked={!form.isActive}
              onChange={(e) => update('isActive', !e.target.checked)}
            />
            Retired — hide from the app. Existing bookings are kept.
          </label>
        )}

        {renaming && (
          <div className="conflict-box">
            <p style={{ fontWeight: 500 }}>
              Renaming “{resource.title}” to “{form.title.trim()}”
            </p>
            <p style={{ marginTop: 3 }}>
              {bookingCount === 0
                ? 'No bookings reference this resource.'
                : `${bookingCount} booking${bookingCount === 1 ? '' : 's'} ` +
                  `will be repointed at the new tag.`}
            </p>
          </div>
        )}

        <div className="form-actions">
          <button
            className="btn-save"
            disabled={saving || !ready}
            onClick={() =>
              onSave({
                ...form,
                title: form.title.trim(),
                name: form.name.trim(),
                category: form.category.trim(),
                office: form.office.trim(),
                notes: form.notes.trim(),
                // Calibration fields are equipment-only; don't carry a
                // stale due date onto a person.
                nextDue: isEquipment ? form.nextDue : '',
                calStatus: isEquipment ? form.calStatus : '',
              })
            }
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add resource'}
          </button>
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
